import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, CircularProgress, Paper, Typography } from '@mui/material';
import { getMedias } from '../../../app/api';
import { useTranslation } from '../../../app/LanguageContext';
import { ReadOnlyMediaCarousel } from '../../medias/public';
import { mediasToCarouselItems } from '../../medias/public/mediaScopeUtils';
import { buildOrderMediaCollectionKey, collectCandidateMediaIds } from './orderMediaCandidates';

export default function OrderMediaRail({
  orderId,
  order,
  formItems,
  refreshKey = 0,
  cardSx,
  headingSx,
}) {
  const { translate } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const orderRef = useRef(order);
  const formItemsRef = useRef(formItems);
  orderRef.current = order;
  formItemsRef.current = formItems;

  const collectionKey = useMemo(
    () => buildOrderMediaCollectionKey(order, formItems, orderId, refreshKey),
    [order, formItems, orderId, refreshKey],
  );

  useEffect(() => {
    if (!orderId) {
      setItems([]);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await getMedias();
        const all = res.data?.medias || [];
        const ids = collectCandidateMediaIds(orderRef.current, formItemsRef.current, orderId, all);
        const medias = [...ids]
          .map((id) => all.find((m) => m.id === id))
          .filter(Boolean)
          .sort((a, b) => String(a.filename || a.id).localeCompare(String(b.filename || b.id)));
        if (!cancelled) {
          setItems(mediasToCarouselItems(medias));
        }
      } catch {
        if (!cancelled) {
          setItems([]);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [orderId, collectionKey]);

  if (!orderId) {
    return null;
  }

  return (
    <Paper elevation={0} sx={cardSx}>
      <Typography component="h2" sx={headingSx} id="order-rail-media-h">
        {translate('orders.media_rail_title')}
      </Typography>
      <Box aria-labelledby="order-rail-media-h">
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          <ReadOnlyMediaCarousel
            items={items}
            compact
            thumbLayout="grid"
            desktopOpenContext={orderId ? { domain: 'order', entityId: orderId } : null}
          />
        )}
      </Box>
    </Paper>
  );
}
